/**
 * Admin Page Component
 *
 * This component provides an admin interface for debugging purposes.
 * It fetches environment variables from the backend and displays them.
 * The page is protected with basic authentication.
 */

import React, { useState, useEffect } from 'react';
import config from '../config';
import OnboardingTab from './OnboardingTab';
import './AdminPage.css';

function AdminPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState('welcome');
  const [sessionDebug, setSessionDebug] = useState({
    sessionId: localStorage.getItem('sessionId') || 'Not found',
    authAttempts: [],
    authResponses: []
  });

  // Add auth attempt to debug info
  const addAuthAttemptToDebug = (type, credentials) => {
    const debugCredentials = { ...credentials };
    if (debugCredentials.password) {
      debugCredentials.password = '********';
    }
    
    setSessionDebug(prev => ({
      ...prev,
      authAttempts: [...prev.authAttempts, {
        timestamp: new Date().toISOString(),
        type,
        credentials: debugCredentials
      }]
    }));
  };

  // Add auth response to debug info
  const addAuthResponseToDebug = (type, status, data) => {
    setSessionDebug(prev => ({
      ...prev,
      authResponses: [...prev.authResponses, {
        timestamp: new Date().toISOString(),
        type,
        status,
        data: JSON.stringify(data, null, 2)
      }]
    }));
  };

  // Function to authenticate with the backend
  const authenticate = async (credentials) => {
    setIsLoading(true);
    setError(null);
    console.log('Authenticating with credentials...', credentials.username ? 'Username provided' : 'No username');

    try {
      // First, check if we have a session ID in local storage
      const sessionId = localStorage.getItem('sessionId');
      
      if (sessionId) {
        // Try to use the existing session
        console.log('Found session ID in localStorage:', sessionId);
        addAuthAttemptToDebug('session-check', { sessionId });
        
        const statusResponse = await fetch(config.endpoints.adminAuthStatus, {
          method: 'GET',
          headers: {
            'X-Session-ID': sessionId,
            'Accept': 'application/json'
          },
          credentials: 'include'
        });
        
        const statusData = await statusResponse.json();
        console.log('Auth status response:', statusResponse.status, statusData);
        addAuthResponseToDebug('session-check', statusResponse.status, statusData);
        
        if (statusResponse.ok && statusData.isAuthenticated) {
          console.log('Session is valid, using existing session');
          setIsAuthenticated(true);
          setUsername(statusData.username);
          
          // Update session debug info
          setSessionDebug(prev => ({
            ...prev,
            sessionId,
            lastChecked: new Date().toISOString(),
            isValid: true
          }));
          
          return true;
        }
        
        // If we get here, the session is invalid or expired
        console.log('Session is invalid or expired, removing from localStorage');
        localStorage.removeItem('sessionId');
        
        // Update session debug info
        setSessionDebug(prev => ({
          ...prev,
          sessionId: 'Removed - Invalid',
          lastChecked: new Date().toISOString(),
          isValid: false
        }));
      }
      
      // If no valid session, try to login with credentials
      if (!credentials.username || !credentials.password) {
        console.log('No credentials provided, not attempting login');
        return false;
      }
      
      // Login with username/password
      console.log('Attempting login with username/password');
      addAuthAttemptToDebug('login', { username: credentials.username });
      
      const loginResponse = await fetch(config.endpoints.adminLogin, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          username: credentials.username,
          password: credentials.password
        }),
        credentials: 'include'
      });

      console.log('Login response status:', loginResponse.status);
      
      // Try to get response as text first
      const responseText = await loginResponse.text();
      console.log('Login response text:', responseText);
      
      // Try to parse as JSON
      let loginData;
      try {
        loginData = JSON.parse(responseText);
        console.log('Login response data:', loginData);
      } catch (e) {
        console.error('Failed to parse login response as JSON:', e);
        loginData = { success: false, error: 'Invalid response format' };
      }
      
      addAuthResponseToDebug('login', loginResponse.status, loginData);

      if (!loginResponse.ok) {
        if (loginResponse.status === 401) {
          throw new Error('Invalid credentials');
        }
        throw new Error(`Server responded with status: ${loginResponse.status}`);
      }

      // Check if we're authenticated
      if (loginData.success) {
        setIsAuthenticated(true);
        setUsername(loginData.username || credentials.username);
        
        // Store session ID in local storage
        const newSessionId = loginData.sessionId || 'session-id-not-provided';
        localStorage.setItem('sessionId', newSessionId);
        console.log('Stored new session ID in localStorage:', newSessionId);
        
        // Update session debug info
        setSessionDebug(prev => ({
          ...prev,
          sessionId: newSessionId,
          lastChecked: new Date().toISOString(),
          isValid: true
        }));
        
        return true;
      } else {
        throw new Error('Authentication failed');
      }
    } catch (err) {
      console.error('Authentication error:', err);
      setError(err.message);
      setIsAuthenticated(false);
      localStorage.removeItem('sessionId');
      
      // Update session debug info
      setSessionDebug(prev => ({
        ...prev,
        sessionId: 'Removed - Error',
        lastChecked: new Date().toISOString(),
        isValid: false,
        lastError: err.message
      }));
      
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Check for existing authentication on component mount
  useEffect(() => {
    const checkAuth = async () => {
      // Try to authenticate with empty credentials
      // This will use the session ID if available
      await authenticate({});
    };
    
    checkAuth();
    
    // Set up interval to check session ID changes
    const sessionCheckInterval = setInterval(() => {
      const currentSessionId = localStorage.getItem('sessionId');
      if (currentSessionId !== sessionDebug.sessionId) {
        setSessionDebug(prev => ({
          ...prev,
          sessionId: currentSessionId || 'Not found'
        }));
      }
    }, 1000);
    
    return () => clearInterval(sessionCheckInterval);
  }, []);

  // Handle login form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    authenticate({ username, password });
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      const sessionId = localStorage.getItem('sessionId');
      
      if (sessionId) {
        // Call logout endpoint
        console.log('Logging out with session ID:', sessionId);
        addAuthAttemptToDebug('logout', { sessionId });
        
        const response = await fetch(config.endpoints.adminLogout, {
          method: 'POST',
          headers: {
            'X-Session-ID': sessionId,
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        });
        
        const responseText = await response.text();
        let responseData;
        try {
          responseData = JSON.parse(responseText);
        } catch (e) {
          responseData = { success: false, error: 'Invalid response format', text: responseText };
        }
        
        console.log('Logout response:', response.status, responseData);
        addAuthResponseToDebug('logout', response.status, responseData);
        
        if (response.ok) {
          console.log('Logged out successfully');
        } else {
          console.error('Error logging out:', response.status);
        }
      }
    } catch (err) {
      console.error('Error during logout:', err);
    } finally {
      // Clear local state regardless of server response
      setIsAuthenticated(false);
      localStorage.removeItem('sessionId');
      
      // Update session debug info
      setSessionDebug(prev => ({
        ...prev,
        sessionId: 'Removed - Logout',
        lastChecked: new Date().toISOString(),
        isValid: false
      }));
    }
  };

  // Function to add session ID to API requests
  const getAuthHeaders = () => {
    const sessionId = localStorage.getItem('sessionId');
    console.log('Getting auth headers with session ID:', sessionId);
    return sessionId ? { 'X-Session-ID': sessionId } : {};
  };

  // Render login form if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="admin-container">
        <div className="admin-card">
          <h1>Admin Login</h1>
          <p>Please enter your admin credentials to access the dashboard.</p>

          {error && <div className="admin-error">{error}</div>}

          <form onSubmit={handleSubmit} className="admin-form">
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <button type="submit" disabled={isLoading}>
              {isLoading ? 'Logging in...' : 'Login'}
            </button>
          </form>
          
          <div className="debug-info">
            <h4>Session Debug Information</h4>
            <p><strong>Session ID:</strong> {sessionDebug.sessionId}</p>
            <p><strong>Last Checked:</strong> {sessionDebug.lastChecked || 'Never'}</p>
            <p><strong>Valid:</strong> {sessionDebug.isValid ? 'Yes' : 'No'}</p>
            {sessionDebug.lastError && <p><strong>Last Error:</strong> {sessionDebug.lastError}</p>}
            
            <h5>Auth Attempts</h5>
            {sessionDebug.authAttempts.map((attempt, index) => (
              <div key={`attempt-${index}`} className="debug-item">
                <p><strong>Time:</strong> {attempt.timestamp}</p>
                <p><strong>Type:</strong> {attempt.type}</p>
                <p><strong>Credentials:</strong></p>
                <pre>{JSON.stringify(attempt.credentials, null, 2)}</pre>
              </div>
            ))}
            
            <h5>Auth Responses</h5>
            {sessionDebug.authResponses.map((response, index) => (
              <div key={`response-${index}`} className="debug-item">
                <p><strong>Time:</strong> {response.timestamp}</p>
                <p><strong>Type:</strong> {response.type}</p>
                <p><strong>Status:</strong> {response.status}</p>
                <p><strong>Data:</strong></p>
                <pre>{response.data}</pre>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Render admin dashboard if authenticated
  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <div className="user-info">
          Logged in as: <strong>{username}</strong>
          <button onClick={handleLogout} className="logout-button">Logout</button>
        </div>
      </div>

      <div className="debug-info">
        <h4>Session Debug Information</h4>
        <p><strong>Session ID:</strong> {sessionDebug.sessionId}</p>
        <p><strong>Last Checked:</strong> {sessionDebug.lastChecked || 'Never'}</p>
        <p><strong>Valid:</strong> {sessionDebug.isValid ? 'Yes' : 'No'}</p>
        {sessionDebug.lastError && <p><strong>Last Error:</strong> {sessionDebug.lastError}</p>}
        
        <h5>Auth Attempts</h5>
        {sessionDebug.authAttempts.map((attempt, index) => (
          <div key={`attempt-${index}`} className="debug-item">
            <p><strong>Time:</strong> {attempt.timestamp}</p>
            <p><strong>Type:</strong> {attempt.type}</p>
            <p><strong>Credentials:</strong></p>
            <pre>{JSON.stringify(attempt.credentials, null, 2)}</pre>
          </div>
        ))}
        
        <h5>Auth Responses</h5>
        {sessionDebug.authResponses.map((response, index) => (
          <div key={`response-${index}`} className="debug-item">
            <p><strong>Time:</strong> {response.timestamp}</p>
            <p><strong>Type:</strong> {response.type}</p>
            <p><strong>Status:</strong> {response.status}</p>
            <p><strong>Data:</strong></p>
            <pre>{response.data}</pre>
          </div>
        ))}
      </div>

      <div className="admin-tabs">
        <button
          className={`tab-button ${activeTab === 'welcome' ? 'active' : ''}`}
          onClick={() => setActiveTab('welcome')}
        >
          Welcome
        </button>
        <button
          className={`tab-button ${activeTab === 'onboarding' ? 'active' : ''}`}
          onClick={() => setActiveTab('onboarding')}
        >
          Onboarding
        </button>
      </div>

      {activeTab === 'welcome' && (
        <div className="admin-tab-content">
          <div className="admin-card welcome-card">
            <h2>Welcome to Gruper Admin</h2>
            <p>Welcome to the Gruper administration panel. This interface allows you to manage WhatsApp channels and user onboarding.</p>
            <p>Use the tabs above to navigate between different sections:</p>
            <ul>
              <li><strong>Welcome</strong>: This welcome page with general information</li>
              <li><strong>Onboarding</strong>: Manage WhatsApp channels and user onboarding</li>
            </ul>
            <p>Current time: {new Date().toLocaleString()}</p>
            <p>Logged in as: <strong>{username}</strong></p>
          </div>
        </div>
      )}

      {activeTab === 'onboarding' && (
        <div className="admin-tab-content">
          <OnboardingTab
            getAuthHeaders={getAuthHeaders}
          />
        </div>
      )}

      <div className="admin-footer">
        <p>Last updated: {new Date().toLocaleString()}</p>
      </div>
    </div>
  );
}

export default AdminPage;
